import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useMessageModal } from "../admin/contexts/MessageModalContext";
import axios from "axios";
import "../styles/shoppingCart.css";

type DiscountType = "fixed" | "percent";

type PromotionData = {
  id: number;
  title: string;
  code: string;
  description?: string;
  apply_type?: "manual_code" | "auto";
  discount_type: DiscountType;
  discount_value: number;
  min_cart_amount?: number;
  min_cart_quantity?: number;
  min_orders_count?: number;
  min_total_spent?: number;
  max_discount_amount?: number | null;
};

type VoucherProduct = {
  id: number;
  productName: string;
};

type FreeGiftProduct = {
  id: number;
  productName: string;
};

type DiscountVoucherData = {
  id: number;
  title: string;
  code: string;
  description?: string;
  voucher_type: "discount" | "free_gift" | "buy_one_get_one";
  discount_type?: DiscountType;
  discount_value?: number;
  min_product_quantity?: number;
  min_product_amount?: number;
  max_discount_amount?: number | null;
  free_gift_quantity?: number;
  buy_quantity?: number;
  free_quantity?: number;
  productIds?: number[];
  products?: VoucherProduct[];
  freeGiftProducts?: FreeGiftProduct[];
};

type AppliedPromotion = PromotionData | null;

const API_BASE_URL =
  import.meta.env.VITE_LYRAZONE_BASE_URL || "http://localhost:5000/api";

const ShoppingCart: React.FC = () => {
  const navigate = useNavigate();
  const { showModal } = useMessageModal();
  const { cartItems, removeFromCart, increaseQty, decreaseQty, clearCart } =
    useCart();
  const [isVoucherDropdownOpen, setIsVoucherDropdownOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [promotionCode, setPromotionCode] = useState("");
  const [appliedPromotion, setAppliedPromotion] =
    useState<AppliedPromotion>(null);

  const [selectedVoucher, setSelectedVoucher] = useState<string>("");

  const [promotions, setPromotions] = useState<PromotionData[]>([]);
  const [discountVouchers, setDiscountVouchers] = useState<
    DiscountVoucherData[]
  >([]);
  const [offersLoading, setOffersLoading] = useState(false);

  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("subscriber_user") || "null");
    } catch {
      return null;
    }
  }, []);

  const userOrdersCount = Number(
    storedUser?.orders_count ||
      storedUser?.total_orders ||
      storedUser?.orderCount ||
      0,
  );

  const userTotalSpent = Number(
    storedUser?.total_spent ||
      storedUser?.lifetime_spent ||
      storedUser?.totalSpent ||
      0,
  );

  const allSelected =
    cartItems.length > 0 && selectedIds.length === cartItems.length;

  const selectedCartItems =
    selectedIds.length === 0
      ? cartItems
      : cartItems.filter((item) => selectedIds.includes(item.id));

  const subtotal = useMemo(() => {
    return selectedCartItems.reduce(
      (sum, item) => sum + Number(item.active_price) * item.quantity,
      0,
    );
  }, [selectedCartItems]);

  const selectedCartQty = useMemo(() => {
    return selectedCartItems.reduce(
      (sum, item) => sum + Number(item.quantity),
      0,
    );
  }, [selectedCartItems]);

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        setOffersLoading(true);

        const [promotionRes, voucherRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/admin/promotions/active/public`),
          axios.get(`${API_BASE_URL}/admin/discount-vouchers/active/public`),
        ]);

        const promotionsData =
          promotionRes.data?.data ||
          promotionRes.data?.promotions ||
          promotionRes.data ||
          [];

        const vouchersData =
          voucherRes.data?.data ||
          voucherRes.data?.discountVouchers ||
          voucherRes.data ||
          [];

        setPromotions(Array.isArray(promotionsData) ? promotionsData : []);
        setDiscountVouchers(Array.isArray(vouchersData) ? vouchersData : []);
      } catch (error) {
        console.error("fetchOffers error:", error);
        setPromotions([]);
        setDiscountVouchers([]);
      } finally {
        setOffersLoading(false);
      }
    };

    fetchOffers();
  }, []);

  const formatRM = (value: number) =>
    `RM ${Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const getItemSubtotal = (items: any[]) =>
    items.reduce(
      (sum, item) =>
        sum + Number(item.active_price) * Number(item.quantity || 0),
      0,
    );

  const getItemQty = (items: any[]) =>
    items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);

  const calculateDiscountValue = (
    baseAmount: number,
    discountType: DiscountType,
    discountValue: number,
    maxDiscountAmount?: number | null,
  ) => {
    if (baseAmount <= 0) return 0;

    let discount =
      discountType === "percent"
        ? (baseAmount * Number(discountValue || 0)) / 100
        : Number(discountValue || 0);

    if (
      maxDiscountAmount !== null &&
      maxDiscountAmount !== undefined &&
      maxDiscountAmount !== 0
    ) {
      discount = Math.min(discount, Number(maxDiscountAmount));
    }

    return Math.min(discount, baseAmount);
  };

  const isPromotionEligible = (promotion: PromotionData, items: any[]) => {
    const itemsSubtotal = getItemSubtotal(items);
    const itemsQty = getItemQty(items);

    if (itemsSubtotal < Number(promotion.min_cart_amount || 0)) return false;
    if (itemsQty < Number(promotion.min_cart_quantity || 0)) return false;
    if (userOrdersCount < Number(promotion.min_orders_count || 0)) return false;
    if (userTotalSpent < Number(promotion.min_total_spent || 0)) return false;

    return true;
  };

  const getVoucherProductIds = (voucher: DiscountVoucherData) => {
    if (Array.isArray(voucher.productIds) && voucher.productIds.length > 0) {
      return voucher.productIds.map((id) => Number(id));
    }

    if (Array.isArray(voucher.products) && voucher.products.length > 0) {
      return voucher.products.map((product) => Number(product.id));
    }

    return [];
  };

  const getVoucherMatchingItems = (
    voucher: DiscountVoucherData,
    items: any[],
  ) => {
    const productIds = getVoucherProductIds(voucher);
    if (!productIds.length) return [];

    return items.filter((item) => productIds.includes(Number(item.id)));
  };

  const isVoucherEligible = (voucher: DiscountVoucherData, items: any[]) => {
    const matchingItems = getVoucherMatchingItems(voucher, items);

    if (!matchingItems.length) return false;

    const matchingQty = getItemQty(matchingItems);
    const matchingSubtotal = getItemSubtotal(matchingItems);

    if (matchingQty < Number(voucher.min_product_quantity || 1)) return false;
    if (matchingSubtotal < Number(voucher.min_product_amount || 0))
      return false;

    return true;
  };

  const autoPromotion = useMemo(() => {
    const autoPromotions = promotions.filter(
      (promotion) => promotion.apply_type === "auto",
    );

    return (
      autoPromotions.find((promotion) =>
        isPromotionEligible(promotion, selectedCartItems),
      ) || null
    );
  }, [promotions, selectedCartItems, userOrdersCount, userTotalSpent]);

  const effectivePromotion = useMemo(() => {
    if (
      appliedPromotion &&
      isPromotionEligible(appliedPromotion, selectedCartItems)
    ) {
      return appliedPromotion;
    }

    return autoPromotion;
  }, [appliedPromotion, autoPromotion, selectedCartItems]);

  const promotionDiscount = useMemo(() => {
    if (!effectivePromotion) return 0;

    return calculateDiscountValue(
      subtotal,
      effectivePromotion.discount_type,
      Number(effectivePromotion.discount_value || 0),
      effectivePromotion.max_discount_amount,
    );
  }, [effectivePromotion, subtotal]);

  const availableVoucherOptions = useMemo(() => {
    return discountVouchers.filter((voucher) =>
      isVoucherEligible(voucher, selectedCartItems),
    );
  }, [discountVouchers, selectedCartItems]);

  const selectedVoucherConfig = useMemo(() => {
    return (
      availableVoucherOptions.find(
        (voucher) => voucher.code === selectedVoucher,
      ) || null
    );
  }, [availableVoucherOptions, selectedVoucher]);

  useEffect(() => {
    if (selectedVoucher && !selectedVoucherConfig) {
      setSelectedVoucher("");
    }
  }, [selectedVoucher, selectedVoucherConfig]);

  useEffect(() => {
    if (
      appliedPromotion &&
      !isPromotionEligible(appliedPromotion, selectedCartItems)
    ) {
      setAppliedPromotion(null);
    }
  }, [appliedPromotion, selectedCartItems]);

  const voucherDiscount = useMemo(() => {
    if (!selectedVoucherConfig) return 0;

    const matchingItems = getVoucherMatchingItems(
      selectedVoucherConfig,
      selectedCartItems,
    );

    if (!matchingItems.length) return 0;

    if (selectedVoucherConfig.voucher_type === "discount") {
      const matchingSubtotal = getItemSubtotal(matchingItems);

      return calculateDiscountValue(
        matchingSubtotal,
        selectedVoucherConfig.discount_type || "fixed",
        Number(selectedVoucherConfig.discount_value || 0),
        selectedVoucherConfig.max_discount_amount,
      );
    }

    if (selectedVoucherConfig.voucher_type === "buy_one_get_one") {
      const buyQty = Math.max(
        Number(selectedVoucherConfig.buy_quantity || 1),
        1,
      );
      const freeQty = Math.max(
        Number(selectedVoucherConfig.free_quantity || 1),
        1,
      );
      const cycleQty = buyQty + freeQty;

      return matchingItems.reduce((sum, item) => {
        const itemQty = Number(item.quantity || 0);
        const freeUnits = Math.floor(itemQty / cycleQty) * freeQty;
        return sum + freeUnits * Number(item.active_price || 0);
      }, 0);
    }

    return 0;
  }, [selectedVoucherConfig, selectedCartItems]);

  const freeGiftSummary = useMemo(() => {
    if (!selectedVoucherConfig) return [];

    if (selectedVoucherConfig.voucher_type !== "free_gift") return [];

    const matchingItems = getVoucherMatchingItems(
      selectedVoucherConfig,
      selectedCartItems,
    );

    if (!matchingItems.length) return [];

    const matchingQty = getItemQty(matchingItems);
    const minQty = Math.max(
      Number(selectedVoucherConfig.min_product_quantity || 1),
      1,
    );

    const triggerCount = Math.floor(matchingQty / minQty);

    if (triggerCount <= 0) return [];

    const giftQty =
      triggerCount * Number(selectedVoucherConfig.free_gift_quantity || 1);

    return (selectedVoucherConfig.freeGiftProducts || []).map((gift) => ({
      ...gift,
      quantity: giftQty,
    }));
  }, [selectedVoucherConfig, selectedCartItems]);

  const finalTotal = Math.max(
    subtotal - promotionDiscount - voucherDiscount,
    0,
  );

  const handleCheckout = () => {
    const storedUser = JSON.parse(
      localStorage.getItem("subscriber_user") || "null",
    );

    if (!storedUser) {
      showModal({
        type: "error",
        title: "Error",
        message: "You are not logged in, please login first.",
      });
      return;
    }

    localStorage.setItem(
      "cart_offers",
      JSON.stringify({
        promotion: effectivePromotion,
        voucher: selectedVoucherConfig,
        promotionDiscount,
        voucherDiscount,
        freeGiftSummary,
        subtotal,
        finalTotal,
        selectedIds,
      }),
    );

    navigate("/checkout");
  };

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(cartItems.map((item) => item.id));
  };

  const handleSelectItem = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((itemId) => itemId !== id)
        : [...prev, id],
    );
  };

  const handleApplyPromotion = () => {
    const code = promotionCode.trim().toUpperCase();

    if (!code) {
      setAppliedPromotion(null);
      showModal({
        type: "success",
        title: "Promotion Cleared",
        message: "Promotion has been removed.",
      });
      return;
    }
    const promotion = promotions.find(
      (item) =>
        String(item.code || "").toUpperCase() === code &&
        (item.apply_type === "manual_code" || !item.apply_type),
    );

    if (!promotion) {
      showModal({
        type: "error",
        title: "Invalid Promotion",
        message: "Promotion code is invalid or not available.",
      });
      return;
    }

    if (!isPromotionEligible(promotion, selectedCartItems)) {
      showModal({
        type: "error",
        title: "Promotion Not Eligible",
        message:
          "This promotion does not meet the required quantity, amount, or customer eligibility.",
      });
      return;
    }

    setAppliedPromotion(promotion);

    showModal({
      type: "success",
      title: "Promotion Applied",
      message: `${promotion.title} applied successfully.`,
    });
  };

  const handleRemoveSelected = () => {
    if (selectedIds.length === 0) return;

    selectedIds.forEach((id) => removeFromCart(id));
    setSelectedIds([]);
  };

  const getVoucherOptionLabel = (voucher: DiscountVoucherData) => {
    if (voucher.voucher_type === "free_gift") {
      return `${voucher.code} - Free Gift`;
    }

    if (voucher.voucher_type === "buy_one_get_one") {
      return `${voucher.code} - Buy ${
        voucher.buy_quantity || 1
      } Get ${voucher.free_quantity || 1}`;
    }

    if (voucher.discount_type === "percent") {
      return `${voucher.code} - ${
        Number(voucher.discount_value || 0) || 0
      }% OFF`;
    }

    return `${voucher.code} - RM ${Number(voucher.discount_value || 0).toFixed(
      2,
    )} OFF`;
  };

  const getVoucherBenefitText = (voucher: DiscountVoucherData) => {
    if (voucher.voucher_type === "free_gift") {
      const gifts =
        voucher.freeGiftProducts?.map((gift) => gift.productName).join(", ") ||
        "Gift Items";

      return `${gifts} x ${voucher.free_gift_quantity || 1}`;
    }

    if (voucher.voucher_type === "buy_one_get_one") {
      return `Buy ${voucher.buy_quantity || 1} Get ${
        voucher.free_quantity || 1
      } Free`;
    }

    if (voucher.discount_type === "percent") {
      return `${Number(voucher.discount_value || 0)}% OFF`;
    }

    return `RM ${Number(voucher.discount_value || 0).toFixed(2)} OFF`;
  };

  return (
    <section className="shopping-cart-page">
      <div className="container">
        <div className="shopping-cart-card">
          <div className="shopping-cart-header">
            <h2>Shopping Cart</h2>
            <p>
              Review your products, apply promotion and voucher, then proceed to
              checkout.
            </p>
          </div>

          {cartItems.length === 0 ? (
            <div className="empty-cart-box">
              <h4>Your cart is empty</h4>
              <p>Add products to see them here.</p>
            </div>
          ) : (
            <>
              <div className="cart-table-header">
                <div className="cart-col cart-col-product">
                  <label className="cart-checkbox-wrap">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={handleSelectAll}
                    />
                    <span>Product</span>
                  </label>
                </div>
                <div className="cart-col">Unit Price</div>
                <div className="cart-col">Quantity</div>
                <div className="cart-col">Total Price</div>
                <div className="cart-col">Actions</div>
              </div>

              <div className="cart-shop-block">
                <div className="cart-shop-title">
                  <label className="cart-checkbox-wrap">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={handleSelectAll}
                    />
                    <span>LyraZone Store</span>
                  </label>
                </div>

                <div className="cart-items-body">
                  {cartItems.map((item) => {
                    const itemTotal = Number(item.active_price) * item.quantity;
                    const isSelected =
                      selectedIds.length === 0
                        ? true
                        : selectedIds.includes(item.id);

                    return (
                      <div key={item.id} className="cart-item-row">
                        <div className="cart-col cart-col-product cart-product-info">
                          <label className="cart-checkbox-wrap">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleSelectItem(item.id)}
                            />
                          </label>

                          <div className="cart-product-image">
                            <img
                              src={`${import.meta.env.VITE_LYRAZONE_IMAGE_URL}/uploads/products/${item.id}/${item.image_path}`}
                              alt={item.productName}
                            />
                          </div>

                          <div className="cart-product-meta">
                            <h4>{item.productName}</h4>
                            <p>Barcode: {item.barCode}</p>
                            <p>Variation: {item.variant || "Default"}</p>
                            <span className="cart-product-tag">In Cart</span>
                          </div>
                        </div>

                        <div className="cart-col cart-price">
                          {formatRM(Number(item.active_price))}
                        </div>

                        <div className="cart-col">
                          <div className="qty-box">
                            <button
                              type="button"
                              onClick={() => decreaseQty(item.id)}
                            >
                              -
                            </button>
                            <span>{item.quantity}</span>
                            <button
                              type="button"
                              onClick={() => increaseQty(item.id)}
                            >
                              +
                            </button>
                          </div>
                        </div>

                        <div className="cart-col cart-total-price">
                          {formatRM(itemTotal)}
                        </div>

                        <div className="cart-col cart-actions">
                          <button
                            className="cart-link-btn"
                            onClick={() => removeFromCart(item.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="cart-discount-grid">
                <div className="discount-card">
                  <h4>Promotion</h4>
                  <div className="discount-row">
                    <input
                      type="text"
                      placeholder="Enter promotion code"
                      value={promotionCode}
                      onChange={(e) => setPromotionCode(e.target.value)}
                    />
                    <button
                      type="button"
                      className="discount-apply-btn"
                      onClick={handleApplyPromotion}
                    >
                      Apply
                    </button>
                  </div>

                  {effectivePromotion && (
                    <p className="discount-success">
                      Applied: <strong>{effectivePromotion.title}</strong>
                    </p>
                  )}
                </div>

                <div className="discount-card">
                  <h4>Discount Voucher</h4>

                  <div className="discount-row">
                    <div
                      className={`custom-select ${isVoucherDropdownOpen ? "open" : ""}`}
                      tabIndex={0}
                      onBlur={() => {
                        setTimeout(() => setIsVoucherDropdownOpen(false), 150);
                      }}
                    >
                      <button
                        type="button"
                        className="custom-select-trigger"
                        onClick={() =>
                          setIsVoucherDropdownOpen((prev) => !prev)
                        }
                      >
                        <span className="custom-select-text">
                          {selectedVoucherConfig
                            ? getVoucherOptionLabel(selectedVoucherConfig)
                            : "Select Voucher"}
                        </span>
                        {/* <span className="custom-select-arrow">&#9662;</span> */}
                      </button>

                      {isVoucherDropdownOpen && (
                        <div className="custom-select-menu">
                          <button
                            // type="button"
                            className={`custom-select-option ${
                              selectedVoucher === "" ? "selected" : ""
                            }`}
                            onClick={() => {
                              setSelectedVoucher("");
                              setIsVoucherDropdownOpen(false);
                            }}
                          >
                            Clear Select
                          </button>

                          {availableVoucherOptions.map((voucher) => (
                            <button
                              type="button"
                              key={voucher.code}
                              className={`custom-select-option ${
                                selectedVoucher === voucher.code
                                  ? "selected"
                                  : ""
                              }`}
                              onClick={() => {
                                setSelectedVoucher(voucher.code);
                                setIsVoucherDropdownOpen(false);
                              }}
                            >
                              {getVoucherOptionLabel(voucher)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {selectedVoucherConfig && (
                    <p className="discount-success">
                      Voucher:{" "}
                      <strong>
                        {getVoucherBenefitText(selectedVoucherConfig)}
                      </strong>
                    </p>
                  )}

                  {offersLoading && (
                    <p className="discount-note">Loading active offers...</p>
                  )}
                </div>
              </div>

              <div className="cart-summary-panel">
                <div className="summary-left-actions">
                  <label className="cart-checkbox-wrap">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={handleSelectAll}
                    />
                    <span>Select All ({cartItems.length})</span>
                  </label>

                  <button
                    type="button"
                    className="summary-action-btn"
                    onClick={handleRemoveSelected}
                  >
                    Delete Selected
                  </button>

                  <button
                    type="button"
                    className="summary-action-btn"
                    onClick={clearCart}
                  >
                    Clear Cart
                  </button>
                </div>

                <div className="summary-right-box">
                  <div className="summary-row">
                    <span>Subtotal</span>
                    <strong>{formatRM(subtotal)}</strong>
                  </div>

                  <div className="summary-row discount">
                    <span>Promotion Discount</span>
                    <strong>- {formatRM(promotionDiscount)}</strong>
                  </div>

                  <div className="summary-row discount">
                    <span>Voucher Benefit</span>
                    <strong>- {formatRM(voucherDiscount)}</strong>
                  </div>

                  {freeGiftSummary.length > 0 && (
                    <div className="summary-free-gifts">
                      <div className="summary-free-gifts-title">
                        Free Gift Items
                      </div>

                      {freeGiftSummary.map((gift) => (
                        <div
                          className="summary-free-gift-row"
                          key={`${gift.id}-${gift.quantity}`}
                        >
                          <span>{gift.productName}</span>
                          <strong>x {gift.quantity}</strong>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="summary-row grand-total">
                    <span>Total ({selectedCartQty} Qty)</span>
                    <strong>{formatRM(finalTotal)}</strong>
                  </div>

                  <button
                    className="checkout-btn"
                    type="button"
                    onClick={handleCheckout}
                  >
                    Check Out
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default ShoppingCart;
